import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';
import { 
  ArrowLeft, Ship, Package, FileText, CheckSquare, Users, Building2, Mail,
  TrendingUp, TrendingDown, BarChart3, PieChartIcon, Activity, RefreshCw,
  Loader2, Globe, ArrowUpFromLine, ArrowDownToLine, CheckCircle, Clock,
  Download, FileDown, Calendar, Settings, Send, Bell, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#263776', '#F47D20', '#22c55e', '#3b82f6', '#a855f7', '#ef4444', '#14b8a6', '#f59e0b'];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' }
];

const Analytics = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [shipmentTrends, setShipmentTrends] = useState([]);
  const [qaStats, setQaStats] = useState(null);
  const [docCompletion, setDocCompletion] = useState(null);
  const [productDist, setProductDist] = useState([]);
  const [routes, setRoutes] = useState([]);
  
  // Schedule settings state
  const [showScheduleSettings, setShowScheduleSettings] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [nextRun, setNextRun] = useState(null);
  const [schedule, setSchedule] = useState({
    enabled: false,
    frequency: 'weekly',
    day_of_week: 0,
    day_of_month: 1,
    hour: 8,
    minute: 0,
    send_to_admin: true,
    send_to_custom: false,
    custom_recipient_email: '',
    include_pdf: true,
    last_sent: null
  });

  useEffect(() => {
    fetchAllAnalytics();
    fetchScheduleSettings();
  }, []);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    try {
      const [overviewRes, trendsRes, qaRes, docRes, productRes, routeRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/overview`),
        fetch(`${API_URL}/api/analytics/shipment-trends`),
        fetch(`${API_URL}/api/analytics/qa-stats`),
        fetch(`${API_URL}/api/analytics/document-completion`),
        fetch(`${API_URL}/api/analytics/product-distribution`),
        fetch(`${API_URL}/api/analytics/route-analysis`)
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (trendsRes.ok) {
        const data = await trendsRes.json();
        setShipmentTrends(data.trends || []);
      }
      if (qaRes.ok) setQaStats(await qaRes.json());
      if (docRes.ok) setDocCompletion(await docRes.json());
      if (productRes.ok) {
        const data = await productRes.json();
        setProductDist(data.products || []);
      }
      if (routeRes.ok) {
        const data = await routeRes.json();
        setRoutes(data.routes || []);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast({
        title: "Error",
        description: "Failed to load analytics data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduleSettings = async () => {
    setScheduleLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const [scheduleRes, nextRunRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/reporting/schedule`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/reporting/next-run`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (scheduleRes.ok) {
        const data = await scheduleRes.json();
        setSchedule(data);
      }
      if (nextRunRes.ok) {
        const data = await nextRunRes.json();
        setNextRun(data);
      }
    } catch (error) {
      console.error('Error fetching schedule:', error);
    } finally {
      setScheduleLoading(false);
    }
  };

  const saveScheduleSettings = async () => {
    setSavingSchedule(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/reporting/schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(schedule)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save schedule');
      }
      
      await fetchScheduleSettings();
      
      toast({
        title: "Schedule Saved",
        description: schedule.enabled ? "Automated reports are now scheduled" : "Automated reports disabled",
      });
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save schedule settings",
        variant: "destructive"
      });
    } finally {
      setSavingSchedule(false);
    }
  };

  const sendTestReport = async () => {
    setSendingTest(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/reporting/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send test report');
      }
      
      toast({
        title: "Test Report Sent",
        description: "Check your email for the analytics report",
      });
    } catch (error) {
      console.error('Error sending test:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send test report",
        variant: "destructive"
      });
    } finally {
      setSendingTest(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllAnalytics();
    setRefreshing(false);
    toast({
      title: "Refreshed",
      description: "Analytics data updated",
    });
  };

  const downloadPdfReport = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${API_URL}/api/analytics/report/pdf`);
      if (!response.ok) throw new Error('Failed to generate report');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SPANXPORT_Analytics_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast({
        title: "Report Downloaded",
        description: "PDF analytics report has been downloaded",
      });
    } catch (error) {
      console.error('Error downloading report:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download report. Please try again.",
        variant: "destructive"
      });
    } finally {
      setDownloading(false);
    }
  };

  // Format month for display
  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const date = new Date(monthStr + '-01');
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  // Prepare QA data for bar chart
  const qaBarData = qaStats?.gate_stats?.map(gate => ({
    name: gate.name.replace('Gate ', 'G'),
    passed: gate.passed,
    failed: gate.failed,
    pending: gate.pending + gate.in_progress
  })) || [];

  // Prepare completion distribution for pie chart
  const completionPieData = docCompletion ? [
    { name: '100%', value: docCompletion.completion_distribution['100'], color: '#22c55e' },
    { name: '76-99%', value: docCompletion.completion_distribution['76-99'], color: '#3b82f6' },
    { name: '51-75%', value: docCompletion.completion_distribution['51-75'], color: '#f59e0b' },
    { name: '26-50%', value: docCompletion.completion_distribution['26-50'], color: '#ef4444' },
    { name: '0-25%', value: docCompletion.completion_distribution['0-25'], color: '#6b7280' }
  ].filter(d => d.value > 0) : [];

  // Product pie data
  const productPieData = productDist.slice(0, 5).map((p, i) => ({
    name: p.product.length > 15 ? p.product.substring(0, 15) + '...' : p.product,
    value: p.total,
    color: COLORS[i % COLORS.length]
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#263776] mx-auto mb-4" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link to="/admin" className="text-sm text-gray-500 hover:text-[#263776] flex items-center gap-1 mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-[#263776] flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-[#F47D20]" />
                Analytics Dashboard
              </h1>
              <p className="text-gray-600 mt-1">Shipment trends, QA performance, and document completion</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={() => setShowScheduleSettings(!showScheduleSettings)}
                variant="outline"
                className="border-[#263776] text-[#263776] hover:bg-[#263776] hover:text-white"
                data-testid="schedule-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Schedule Reports
                {showScheduleSettings ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
              </Button>
              <Button 
                onClick={downloadPdfReport} 
                disabled={downloading}
                variant="outline"
                className="border-[#F47D20] text-[#F47D20] hover:bg-[#F47D20] hover:text-white"
                data-testid="download-report-btn"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileDown className="w-4 h-4 mr-2" />
                )}
                Download Report
              </Button>
              <Button 
                onClick={handleRefresh} 
                disabled={refreshing}
                className="bg-[#263776] hover:bg-[#1e2d5f]"
              >
                {refreshing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh Data
              </Button>
            </div>
          </div>
        </div>

        {/* Schedule Settings Panel */}
        {showScheduleSettings && (
          <Card className="mb-8 border-2 border-[#263776]/20" data-testid="schedule-settings-panel">
            <CardHeader className="bg-gradient-to-r from-[#263776]/5 to-[#F47D20]/5">
              <CardTitle className="flex items-center gap-2 text-[#263776]">
                <Calendar className="w-5 h-5 text-[#F47D20]" />
                Automated Report Schedule
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {scheduleLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-[#263776]" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Enable/Disable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Bell className={`w-5 h-5 ${schedule.enabled ? 'text-green-500' : 'text-gray-400'}`} />
                      <div>
                        <Label className="text-base font-medium">Enable Automated Reports</Label>
                        <p className="text-sm text-gray-500">Automatically send analytics reports on schedule</p>
                      </div>
                    </div>
                    <Switch
                      checked={schedule.enabled}
                      onCheckedChange={(checked) => setSchedule({...schedule, enabled: checked})}
                      data-testid="schedule-enable-toggle"
                    />
                  </div>

                  {/* Schedule Options */}
                  <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${!schedule.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                    {/* Frequency */}
                    <div className="space-y-2">
                      <Label>Frequency</Label>
                      <Select
                        value={schedule.frequency}
                        onValueChange={(value) => setSchedule({...schedule, frequency: value})}
                      >
                        <SelectTrigger data-testid="frequency-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Day of Week (for weekly) */}
                    {schedule.frequency === 'weekly' && (
                      <div className="space-y-2">
                        <Label>Day of Week</Label>
                        <Select
                          value={String(schedule.day_of_week)}
                          onValueChange={(value) => setSchedule({...schedule, day_of_week: parseInt(value)})}
                        >
                          <SelectTrigger data-testid="day-of-week-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DAYS_OF_WEEK.map(day => (
                              <SelectItem key={day.value} value={String(day.value)}>{day.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Day of Month (for monthly) */}
                    {schedule.frequency === 'monthly' && (
                      <div className="space-y-2">
                        <Label>Day of Month</Label>
                        <Select
                          value={String(schedule.day_of_month)}
                          onValueChange={(value) => setSchedule({...schedule, day_of_month: parseInt(value)})}
                        >
                          <SelectTrigger data-testid="day-of-month-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({length: 28}, (_, i) => i + 1).map(day => (
                              <SelectItem key={day} value={String(day)}>{day}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Time */}
                    <div className="space-y-2">
                      <Label>Time (Hour)</Label>
                      <Select
                        value={String(schedule.hour)}
                        onValueChange={(value) => setSchedule({...schedule, hour: parseInt(value)})}
                      >
                        <SelectTrigger data-testid="hour-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({length: 24}, (_, i) => i).map(hour => (
                            <SelectItem key={hour} value={String(hour)}>
                              {hour.toString().padStart(2, '0')}:00 {hour < 12 ? 'AM' : 'PM'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Minute */}
                    <div className="space-y-2">
                      <Label>Minute</Label>
                      <Select
                        value={String(schedule.minute)}
                        onValueChange={(value) => setSchedule({...schedule, minute: parseInt(value)})}
                      >
                        <SelectTrigger data-testid="minute-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[0, 15, 30, 45].map(min => (
                            <SelectItem key={min} value={String(min)}>{min.toString().padStart(2, '0')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Recipients */}
                  <div className={`space-y-4 ${!schedule.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                    <Label className="text-base font-medium">Recipients</Label>
                    
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Switch
                        checked={schedule.send_to_admin}
                        onCheckedChange={(checked) => setSchedule({...schedule, send_to_admin: checked})}
                        data-testid="send-to-admin-toggle"
                      />
                      <div>
                        <Label>Send to Admin Email</Label>
                        <p className="text-sm text-gray-500">pmonga@spanxport.com</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Switch
                        checked={schedule.send_to_custom}
                        onCheckedChange={(checked) => setSchedule({...schedule, send_to_custom: checked})}
                        className="mt-1"
                        data-testid="send-to-custom-toggle"
                      />
                      <div className="flex-1 space-y-2">
                        <Label>Send to Custom Email</Label>
                        <Input
                          type="email"
                          placeholder="additional@email.com"
                          value={schedule.custom_recipient_email}
                          onChange={(e) => setSchedule({...schedule, custom_recipient_email: e.target.value})}
                          disabled={!schedule.send_to_custom}
                          className="max-w-md"
                          data-testid="custom-email-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* PDF Attachment Option */}
                  <div className={`flex items-center gap-3 p-3 bg-gray-50 rounded-lg ${!schedule.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                    <Switch
                      checked={schedule.include_pdf}
                      onCheckedChange={(checked) => setSchedule({...schedule, include_pdf: checked})}
                      data-testid="include-pdf-toggle"
                    />
                    <div>
                      <Label>Include PDF Attachment</Label>
                      <p className="text-sm text-gray-500">Attach a PDF version of the analytics report</p>
                    </div>
                  </div>

                  {/* Status and Actions */}
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-4 border-t">
                    <div className="space-y-1">
                      {nextRun?.scheduled && (
                        <p className="text-sm text-gray-600">
                          <Clock className="w-4 h-4 inline mr-1" />
                          Next report: <span className="font-medium text-[#263776]">{nextRun.next_run_formatted}</span>
                        </p>
                      )}
                      {schedule.last_sent && (
                        <p className="text-sm text-gray-500">
                          Last sent: {new Date(schedule.last_sent).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={sendTestReport}
                        disabled={sendingTest}
                        variant="outline"
                        className="border-[#F47D20] text-[#F47D20] hover:bg-[#F47D20] hover:text-white"
                        data-testid="send-test-btn"
                      >
                        {sendingTest ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Send Test Report
                      </Button>
                      <Button
                        onClick={saveScheduleSettings}
                        disabled={savingSchedule}
                        className="bg-[#263776] hover:bg-[#1e2d5f]"
                        data-testid="save-schedule-btn"
                      >
                        {savingSchedule ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4 mr-2" />
                        )}
                        Save Schedule
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Total Shipments</p>
                  <p className="text-3xl font-bold">{overview?.total_shipments || 0}</p>
                </div>
                <Ship className="w-10 h-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Exports</p>
                  <p className="text-3xl font-bold">{overview?.export_shipments || 0}</p>
                </div>
                <ArrowUpFromLine className="w-10 h-10 text-green-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Imports</p>
                  <p className="text-3xl font-bold">{overview?.import_shipments || 0}</p>
                </div>
                <ArrowDownToLine className="w-10 h-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Completed</p>
                  <p className="text-3xl font-bold">{overview?.completed_shipments || 0}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm">QA Pass Rate</p>
                  <p className="text-3xl font-bold">{qaStats?.pass_rate || 0}%</p>
                </div>
                <CheckSquare className="w-10 h-10 text-orange-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-cyan-100 text-sm">Avg Doc %</p>
                  <p className="text-3xl font-bold">{docCompletion?.average_completion || 0}%</p>
                </div>
                <Package className="w-10 h-10 text-cyan-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Shipment Trends */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#263776]">
                <TrendingUp className="w-5 h-5 text-[#F47D20]" />
                Shipment Trends
              </CardTitle>
            </CardHeader>
            <CardContent>
              {shipmentTrends.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={shipmentTrends}>
                    <defs>
                      <linearGradient id="colorExports" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorImports" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tickFormatter={formatMonth} fontSize={12} />
                    <YAxis fontSize={12} />
                    <Tooltip 
                      labelFormatter={formatMonth}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="exports" name="Exports" stroke="#22c55e" fillOpacity={1} fill="url(#colorExports)" />
                    <Area type="monotone" dataKey="imports" name="Imports" stroke="#3b82f6" fillOpacity={1} fill="url(#colorImports)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500">
                  <p>No shipment data available</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* QA Gates Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#263776]">
                <CheckSquare className="w-5 h-5 text-[#F47D20]" />
                QA Gates Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {qaBarData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={qaBarData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" fontSize={12} />
                    <YAxis fontSize={12} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                    <Legend />
                    <Bar dataKey="passed" name="Passed" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="failed" name="Failed" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="pending" name="Pending" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500">
                  <p>No QA data available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {/* Document Completion Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#263776]">
                <PieChartIcon className="w-5 h-5 text-[#F47D20]" />
                Document Completion
              </CardTitle>
            </CardHeader>
            <CardContent>
              {completionPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={completionPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {completionPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-gray-500">
                  <p>No document data</p>
                </div>
              )}
              <div className="text-center mt-2">
                <p className="text-sm text-gray-500">
                  {docCompletion?.total_packs || 0} Export Packs
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Product Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#263776]">
                <Package className="w-5 h-5 text-[#F47D20]" />
                Top Products
              </CardTitle>
            </CardHeader>
            <CardContent>
              {productPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={productPieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      dataKey="value"
                      label={({ name, value }) => `${value}`}
                    >
                      {productPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-gray-500">
                  <p>No product data</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Trade Routes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#263776]">
                <Globe className="w-5 h-5 text-[#F47D20]" />
                Top Trade Routes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {routes.length > 0 ? (
                <div className="space-y-3">
                  {routes.slice(0, 5).map((route, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-[#263776] text-white text-xs flex items-center justify-center font-bold">
                          {idx + 1}
                        </span>
                        <span className="text-sm font-medium truncate max-w-[150px]">{route.route}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={route.type === 'Import' ? 'text-blue-600' : 'text-green-600'}>
                          {route.type}
                        </Badge>
                        <span className="font-bold text-[#263776]">{route.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-gray-500">
                  <p>No route data</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detailed Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* QA Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#263776]">
                <Activity className="w-5 h-5 text-[#F47D20]" />
                QA Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-3xl font-bold text-green-600">{qaStats?.overall?.passed || 0}</p>
                  <p className="text-sm text-green-700">Gates Passed</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-3xl font-bold text-red-600">{qaStats?.overall?.failed || 0}</p>
                  <p className="text-sm text-red-700">Gates Failed</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-3xl font-bold text-blue-600">{qaStats?.overall?.in_progress || 0}</p>
                  <p className="text-sm text-blue-700">In Progress</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <p className="text-3xl font-bold text-yellow-600">{qaStats?.overall?.pending || 0}</p>
                  <p className="text-sm text-yellow-700">Pending</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total QA Records</span>
                  <span className="font-bold text-[#263776]">{qaStats?.total_records || 0}</span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-gray-600">Overall Pass Rate</span>
                  <span className="font-bold text-green-600">{qaStats?.pass_rate || 0}%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Directory Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#263776]">
                <Users className="w-5 h-5 text-[#F47D20]" />
                Directory Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <Mail className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-orange-600">{overview?.total_contacts || 0}</p>
                  <p className="text-sm text-orange-700">Contacts</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-600">{overview?.total_buyers || 0}</p>
                  <p className="text-sm text-blue-700">Buyers</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <Building2 className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-purple-600">{overview?.total_sellers || 0}</p>
                  <p className="text-sm text-purple-700">Sellers</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-gray-600 text-center">
                  EximStack Records: {overview?.total_dossiers || 0} Dossiers, {overview?.total_qa_records || 0} QA Records, {overview?.total_export_packs || 0} Export Packs
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
